import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { cities, regions } from '../constants/locations';

// ─── Small presentational helpers used across all form steps ──────────────

export function FieldLabel({ children, valid, touched }) {
  return (
    <label className="tu-label">
      {children}
      <span className={`tu-required ${touched ? (valid ? 'tu-valid' : 'tu-invalid') : ''}`}>
        {touched && valid ? <FontAwesomeIcon icon={faCheck} /> : '*'}
      </span>
    </label>
  );
}

export function ChoiceCard({ selected, onClick, icon, children }) {
  return (
    <button type="button" className={`tu-choice-card ${selected ? 'is-selected' : ''}`} onClick={onClick}>
      {icon && <FontAwesomeIcon icon={icon} className="tu-choice-icon" />}
      <span>{children}</span>
      {selected && <FontAwesomeIcon icon={faCheck} className="tu-choice-check" />}
    </button>
  );
}

// City / Region cascading dropdown picker, used in both the "Flat Details"
// and "Looking For" steps.
//
// By default the region field is a single value (string), same as city.
// Pass `regionMultiSelect` to let the region field hold an array of values
// instead (used by the "Looking For" step so a user without a specific flat
// yet can shortlist a few regions) — pass `handleRegionToggle` in that case,
// and `handleRegionSelect` otherwise. `maxRegions` caps how many can be picked.
export function LocationPicker({
  label,
  cityField,
  regionField,
  formData,
  isFieldValid,
  touched,
  isCityDropdownOpen,
  setIsCityDropdownOpen,
  isRegionDropdownOpen,
  setIsRegionDropdownOpen,
  citySelectorRef,
  regionSelectorRef,
  handleCitySelect,
  handleRegionSelect,
  handleRegionToggle,
  regionMultiSelect = false,
  maxRegions = 4,
  getLocalizedText,
}) {
  const regionList = regions[formData[cityField]] || [];
  const selectedRegionValues = regionMultiSelect ? (formData[regionField] || []) : null;
  const isMaxReached = regionMultiSelect && selectedRegionValues.length >= maxRegions;

  const regionDisplayText = regionMultiSelect
    ? (selectedRegionValues.length > 0
        ? selectedRegionValues
            .map((value) => {
              const match = regionList.find((r) => r.value === value);
              return match ? getLocalizedText(match.ar, match.en) : value;
            })
            .join(getLocalizedText('، ', ', '))
        : getLocalizedText('المنطقة', 'Region'))
    : (formData[regionField]
        ? getLocalizedText(
            regionList.find((r) => r.value === formData[regionField])?.ar,
            regionList.find((r) => r.value === formData[regionField])?.en
          )
        : getLocalizedText('المنطقة', 'Region'));

  return (
    <div className="tu-field">
      <FieldLabel valid={isFieldValid(cityField) && isFieldValid(regionField)} touched={touched}>
        {label}
      </FieldLabel>
      <div className="tu-location-row">
        <div className="tu-select-wrapper" ref={citySelectorRef} onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}>
          <div className="tu-select-display">
            <span>{formData[cityField] ? getLocalizedText(
              cities.find((c) => c.value === formData[cityField])?.ar,
              cities.find((c) => c.value === formData[cityField])?.en
            ) : getLocalizedText('المدينة', 'City')}</span>
            <FontAwesomeIcon icon={faChevronDown} className="tu-select-icon" />
          </div>
          {isCityDropdownOpen && (
            <div className="tu-dropdown-menu">
              {cities.map((city, i) => (
                <div key={i} className="tu-dropdown-item" onClick={(e) => { e.stopPropagation(); handleCitySelect(cityField, city); }}>
                  {getLocalizedText(city.ar, city.en)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className="tu-select-wrapper"
          ref={regionSelectorRef}
          onClick={() => formData[cityField] && setIsRegionDropdownOpen(!isRegionDropdownOpen)}
        >
          <div className={`tu-select-display ${!formData[cityField] ? 'is-disabled' : ''}`}>
            <span>{regionDisplayText}</span>
            <FontAwesomeIcon icon={faChevronDown} className="tu-select-icon" />
          </div>
          {isRegionDropdownOpen && formData[cityField] && (
            <div className="tu-dropdown-menu">
              {regionList.map((region, i) => {
                if (!regionMultiSelect) {
                  return (
                    <div key={i} className="tu-dropdown-item" onClick={(e) => { e.stopPropagation(); handleRegionSelect(regionField, region); }}>
                      {getLocalizedText(region.ar, region.en)}
                    </div>
                  );
                }
                const isSelected = selectedRegionValues.includes(region.value);
                const isDisabled = !isSelected && isMaxReached;
                return (
                  <div
                    key={i}
                    className={`tu-dropdown-item ${isSelected ? 'is-selected' : ''} ${isDisabled ? 'is-disabled' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDisabled) return;
                      handleRegionToggle(regionField, region);
                    }}
                  >
                    <span>{getLocalizedText(region.ar, region.en)}</span>
                    {isSelected && <FontAwesomeIcon icon={faCheck} className="tu-dropdown-check" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {regionMultiSelect && (
        <div className="tu-select-hint">
          {getLocalizedText(`يمكنك اختيار حتى ${maxRegions} مناطق`, `You can select up to ${maxRegions} regions`)}
        </div>
      )}
    </div>
  );
}